import { type FC, type ReactElement, type ReactNode, memo } from 'react';
import { type auto } from 'angular';
import { Provider } from 'react-redux';
import { AngularProvider } from 'AngularContext';
import { useTargetBrandConfig } from 'FrontRoyalAngular/useTargetBrandConfig';
import clsx from 'clsx';
import { type configureStore } from '@reduxjs/toolkit';
import { HeroUIProvider } from '@heroui/react';
import { TanStackQueryProvider } from 'BackRoyalApi';

export function BrandContainer({ children, className }: { children: ReactNode; className?: string }) {
    const targetBrandConfig = useTargetBrandConfig();
    return (
        <div
            className={clsx(
                // The brandStyleClass controls the heroui theme defined in the tailwind config (if it is set to valar).
                // It also controls the variants like `quantic:` `valar:` etc. (see this code in tailwind.config.js: `addVariant(brand, `.${brand} &`)`)
                // We do something similar in setBodyBackground, but there we're targeting deprecated css.
                targetBrandConfig.brandStyleClass,
                className,
            )}
        >
            {children}
        </div>
    );
}

type Props = {
    children: ReactElement;
    angularInjector: auto.IInjectorService;
    classNames?: string;
    provideHeroUi?: boolean;
    store: ReturnType<typeof configureStore>;
};

const ReactWrapperComponent: FC<Props> = ({ children, angularInjector, classNames, provideHeroUi = true, store }) => {
    const appProviders = (
        <TanStackQueryProvider>
            <Provider store={store}>{children}</Provider>
        </TanStackQueryProvider>
    );

    return (
        <AngularProvider value={angularInjector}>
            {/* h-full is because we ran into the problem described at https://github.com/heroui-inc/heroui/issues/1494. See https://trello.com/c/DG8Rob7G */}
            {provideHeroUi ? (
                <HeroUIProvider className={clsx({ 'h-full': classNames?.match('h-full') })}>
                    <BrandContainer className={classNames}>{appProviders}</BrandContainer>
                </HeroUIProvider>
            ) : (
                <BrandContainer className={classNames}>{appProviders}</BrandContainer>
            )}
        </AngularProvider>
    );
};

export const ReactWrapper = memo(ReactWrapperComponent) as typeof ReactWrapperComponent;
